import { pageDocument } from '../doc-blocks'
// Block drop: the move a block handle drag ends in. Donor: the Lexical view's DraggableBlockPlugin drop
// (`block-actions-plugin.tsx` MOVE_BLOCK_COMMAND), which this rule now serves for every view.
import { questionIds, unsavedCopy } from './unsaved-copy'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { pastedComments, pasteIntent, selectedFragment, type ClipboardData } from './clipboard'
import type { EditorIntent } from './intents'
import { moveBlock, spliceBlocks } from './ops'
import type { CorePoint, CoreSelection, CoreState } from './state'
import { children, firstPoint, gapOf, isRow, locate, rows } from './tree-read'
import { isInlineContent } from './levels'

export type DropPlacement = 'before' | 'after' | 'inside'

/** Where block `id` sits: its list, and the blocks above it. */
function find(blocks: Blocks, id: string): { block: Block; items: Blocks; ancestors: Block[] } | undefined {
  const visit = (items: Blocks, ancestors: Block[]): ReturnType<typeof find> => {
    for (const block of items) {
      if (block.id === id) return { block, items, ancestors }
      const found = visit(children(block), [...ancestors, block])
      if (found) return found
    }
    return undefined
  }
  return visit(blocks, [])
}

/**
 * Moves the blocks `sources` (ids, kept in document order) before or after block `target`, or to the end of its
 * children (`inside`). False, with nothing moved, when a block is unknown, a source is the target, or the target sits
 * inside a source, or the blocks would land in a text row's inline content. `copy` (an Alt-drag, Notion keyboard
 * shortcuts "Duplicate by dragging") leaves the sources and drops copies with fresh identities, as Mod+D does, whose
 * comment refs become plain text; the caret goes to the first copy's start.
 */
export function dropBlocks(state: CoreState, sources: readonly string[], target: string, placement: DropPlacement, copy = false): boolean {
  const destination = find(state.blocks, target)
  const unique = [...new Set(sources)]
  const origins = unique.flatMap((id) => find(state.blocks, id) ?? [])
  if (!destination || origins.length === 0 || origins.length < unique.length || unique.includes(target)) return false
  if (destination.ancestors.some((ancestor) => unique.includes(ancestor.id))) return false
  // A text row (a paragraph, heading or table cell) holds inline content only: block content never drops into it
  // (INV-LEVEL).
  const owner = placement === 'inside' ? destination.block : destination.ancestors.at(-1)
  if (owner && isRow(owner) && origins.some(origin => !isInlineContent(origin.block))) return false
  const order = (id: string) => {
    let index = 0
    let at = -1
    const walk = (items: Blocks) => {
      for (const block of items) {
        if (block.id === id) at = index
        index += 1
        walk(children(block))
      }
    }
    walk(state.blocks)
    return at
  }
  // Moves splice lists in place, so each origin's list stays the one holding its block.
  const ordered = origins.toSorted((left, right) => order(left.block.id) - order(right.block.id))
  const list = placement === 'inside' ? children(destination.block) : destination.items
  if (copy) {
    const qids = new Map<string, string>()
    // A page names each thread once: the copy's comment refs become their plain text, as a paste beside its source does.
    const copies = pastedComments(ordered.map((origin) => unsavedCopy(origin.block, qids)), pageDocument(state.doc).content)
    spliceBlocks(state, list, placement === 'inside' ? list.length : list.indexOf(destination.block) + (placement === 'after' ? 1 : 0), 0, ...copies)
    const start = firstPoint(copies)
    state.selection = { anchor: start, focus: start }
    return true
  }
  for (const origin of placement === 'after' ? ordered.toReversed() : ordered) {
    let at = placement === 'inside' ? list.length : list.indexOf(destination.block) + (placement === 'after' ? 1 : 0)
    const from = origin.items.indexOf(origin.block)
    if (origin.items === list && from < at) at -= 1
    if (origin.items !== list || from !== at) moveBlock(state, origin.block, list, at)
  }
  return true
}

/**
 * Where `point` falls against the text range `selection` (rows and offsets, ends inclusive): -1 before it, 0 inside,
 * 1 after it; null when the selection is no draggable text range (collapsed, blocks, cells or a gap end).
 */
export function textDropSide(blocks: Blocks, selection: CoreSelection | null, point: CorePoint): -1 | 0 | 1 | null {
  if (!selection || selection.nodes?.length || selection.table) return null
  if (gapOf(blocks, selection.anchor) || gapOf(blocks, selection.focus) || gapOf(blocks, point)) return null
  const entries = rows(blocks)
  const order = (a: { row: number; offset: number }, b: { row: number; offset: number }) => a.row - b.row || a.offset - b.offset
  const [start, end] = [locate(entries, selection.anchor), locate(entries, selection.focus)].toSorted(order)
  if (order(start, end) === 0) return null
  const at = locate(entries, point)
  return order(at, start) < 0 ? -1 : order(at, end) > 0 ? 1 : 0
}

/**
 * A text drop at `at` (Lexical rich-text DROP_COMMAND, with the browser's move of the dragged selection): `data` from
 * outside the editor is inserted there; otherwise the selection moves there, nothing happening inside its own range.
 * The edit nearer the document end runs first, so `at` and the range keep their positions; the caret ends after the
 * dropped content. `apply` runs a command in the same transaction.
 */
export function dropText(state: CoreState, at: CorePoint, data: ClipboardData | undefined, apply: (intent: EditorIntent) => void): void {
  const insert = (intent: EditorIntent) => {
    state.selection = { anchor: at, focus: at }
    apply(intent)
    return state.selection
  }
  if (data) {
    insert(pasteIntent(data, () => questionIds(pageDocument(state.doc).content)))
    return
  }
  const side = textDropSide(state.blocks, state.selection, at)
  if (!side || !state.selection) return
  const range = structuredClone(state.selection)
  const dragged: EditorIntent = { type: 'insertFromPaste', md: '', fragment: selectedFragment(state).fragment }
  const remove = () => {
    state.selection = range
    apply({ type: 'deleteContentBackward' })
  }
  if (side > 0) {
    const caret = insert(dragged)
    remove()
    state.selection = caret
  } else {
    remove()
    insert(dragged)
  }
}
